import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { useApiList, useMutation, type Query } from "../api/hooks";
import { uploadFile } from "../api/upload";
import { type Column, DataTable, Field, Modal, Page, Pagination, SearchBox, Switch, Toolbar, useConfirm, useToast } from "./ui";

export type FieldSpec = {
  name: string;
  label: string;
  type?: "text" | "number" | "textarea" | "switch" | "select" | "color" | "date" | "password" | "multiselect" | "image";
  options?: { value: string; label: string }[] | (() => { value: string; label: string }[]);
  required?: boolean;
  hint?: string;
  /** Only shown when creating (e.g. passwords). */
  createOnly?: boolean;
  /** Only shown when editing. */
  editOnly?: boolean;
  /** Sent as a decimal string (`"12.50"`) instead of a number. */
  decimal?: boolean;
  placeholder?: string;
  /** Initial value when creating (switches default to on otherwise). */
  defaultValue?: unknown;
};

export type FilterSpec = { name: string; label: string; options: { value: string; label: string }[] };

export interface CrudPageProps<T extends { id: string }> {
  title: string;
  /** `/admin/<resource>` unless `basePath` is given. */
  resource: string;
  basePath?: string;
  columns: Column<T>[];
  fields: FieldSpec[];
  canCreate?: boolean;
  canDelete?: boolean;
  canEdit?: boolean;
  searchable?: boolean;
  filters?: FilterSpec[];
  /** Extra constant query parameters for the list. */
  listQuery?: Query;
  /** Query parameters sent with create/update/delete (e.g. admin `vendor_id`). */
  mutationQuery?: Query;
  /** Turn a row into form values (defaults to the row itself). */
  toForm?: (row: T) => Record<string, unknown>;
  /** Fetch fuller values before editing (e.g. a detail endpoint). */
  loadForEdit?: (row: T) => Promise<Record<string, unknown>>;
  /** Final transform before sending. */
  toBody?: (values: Record<string, unknown>, editing: T | null) => Record<string, unknown>;
  /** Row-level extra actions. */
  rowActions?: (row: T, reload: () => void) => ReactNode;
  /** Server paging (`page`, `per_page`) supported? */
  paged?: boolean;
  /** Content rendered under the toolbar (e.g. tabs). */
  children?: ReactNode;
  perPage?: number;
}

/**
 * Config-driven list + create/edit modal + delete for the simple admin
 * resources (vendor types, categories, banners, coupons, …).
 */
export function CrudPage<T extends { id: string }>(props: CrudPageProps<T>) {
  const {
    title,
    resource,
    basePath,
    columns,
    fields,
    canCreate = true,
    canDelete = true,
    canEdit = true,
    searchable = false,
    filters = [],
    listQuery = {},
    mutationQuery,
    toForm,
    loadForEdit,
    toBody,
    rowActions,
    paged = true,
    children,
    perPage = 25,
  } = props;
  const base = basePath ?? `/admin/${resource}`;
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const query = useMemo<Query>(
    () => ({ ...listQuery, ...(paged ? { page, per_page: perPage } : {}), q: q || undefined, ...filterValues }),
    [listQuery, paged, page, perPage, q, filterValues],
  );
  const list = useApiList<T>(base, query);
  const [editing, setEditing] = useState<T | null | "new">(null);
  const [loaded, setLoaded] = useState<Record<string, unknown> | null>(null);
  const { confirm, dialog } = useConfirm();
  async function openEdit(row: T) {
    if (loadForEdit) {
      try {
        setLoaded(await loadForEdit(row));
      } catch (e) {
        toast(e instanceof Error ? e.message : "could not load", "err");
        return;
      }
    } else setLoaded(null);
    setEditing(row);
  }
  const { run, busy } = useMutation();
  const toast = useToast();

  const onSearch = useCallback((v: string) => {
    setQ(v);
    setPage(1);
  }, []);

  async function remove(row: T) {
    if (!(await confirm("Delete this item? This cannot be undone."))) return;
    const r = await run(`${base}/${row.id}`, { method: "DELETE", query: mutationQuery });
    if (r.ok) {
      toast("Deleted");
      list.reload();
    } else toast(r.error, "err");
  }

  return (
    <Page
      title={title}
      actions={
        canCreate ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing("new")}>
            + New
          </button>
        ) : null
      }
    >
      {(searchable || filters.length > 0) && (
        <Toolbar>
          {searchable && <SearchBox value={q} onChange={onSearch} />}
          {filters.map((f) => (
            <select
              key={f.name}
              className="input"
              value={filterValues[f.name] ?? ""}
              onChange={(e) => {
                setFilterValues((v) => ({ ...v, [f.name]: e.target.value }));
                setPage(1);
              }}
            >
              <option value="">{f.label}: all</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ))}
        </Toolbar>
      )}
      {children}
      <DataTable
        columns={[
          ...columns,
          ...(canEdit || canDelete || rowActions
            ? [
                {
                  key: "_actions",
                  label: "",
                  align: "right" as const,
                  render: (row: T) => (
                    <span className="row-actions" onClick={(e) => e.stopPropagation()}>
                      {rowActions?.(row, list.reload)}
                      {canEdit && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(row)}>
                          Edit
                        </button>
                      )}
                      {canDelete && (
                        <button type="button" className="btn btn-ghost btn-sm danger" onClick={() => remove(row)}>
                          Delete
                        </button>
                      )}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
        rows={list.data}
        loading={list.loading}
        error={list.error}
        onRowClick={canEdit ? (row) => openEdit(row) : undefined}
      />
      {paged && <Pagination page={page} perPage={perPage} total={list.total} onPage={setPage} />}
      {editing !== null && (
        <CrudForm
          title={editing === "new" ? `New ${singular(title)}` : `Edit ${singular(title)}`}
          fields={fields}
          initial={editing === "new" ? {} : (loaded ?? (toForm ? toForm(editing) : (editing as unknown as Record<string, unknown>)))}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            const body = toBody ? toBody(values, editing === "new" ? null : editing) : values;
            const r =
              editing === "new"
                ? await run(base, { method: "POST", body, query: mutationQuery })
                : await run(`${base}/${editing.id}`, { method: "PATCH", body, query: mutationQuery });
            if (r.ok) {
              toast("Saved");
              setEditing(null);
              list.reload();
            } else toast(r.error, "err");
          }}
        />
      )}
      {dialog}
    </Page>
  );
}

function singular(title: string) {
  return title.endsWith("ies") ? `${title.slice(0, -3)}y` : title.endsWith("s") ? title.slice(0, -1) : title;
}

/** Modal form driven by `FieldSpec`s. Only touched fields are sent on edit. */
export function CrudForm({
  title,
  fields,
  initial,
  busy,
  onClose,
  onSubmit,
  isNew: isNewProp,
}: {
  title: string;
  fields: FieldSpec[];
  initial: Record<string, unknown>;
  busy: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  /** Defaults to "no initial values". Pass explicitly when creating with presets. */
  isNew?: boolean;
}) {
  const isNew = isNewProp ?? Object.keys(initial).length === 0;
  const visible = fields.filter((f) => (isNew ? !f.editOnly : !f.createOnly));
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const f of visible) {
      const cur = initial[f.name];
      v[f.name] = cur ?? f.defaultValue ?? (f.type === "switch" ? true : f.type === "multiselect" ? [] : "");
    }
    return v;
  });
  const [touched, setTouched] = useState<Set<string>>(new Set());
  useEffect(() => {
    setTouched(new Set());
  }, [initial]);

  function set(name: string, value: unknown) {
    setValues((v) => ({ ...v, [name]: value }));
    setTouched((t) => new Set(t).add(name));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const out: Record<string, unknown> = {};
    for (const f of visible) {
      if (!isNew && !touched.has(f.name)) continue;
      let v = values[f.name];
      if (v === "" || v === undefined) {
        if (f.required && isNew) {
          return;
        }
        if (isNew) continue;
        v = null;
      }
      if (f.type === "number") v = f.decimal ? Number(v).toFixed(2) : Number(v);
      out[f.name] = v;
    }
    await onSubmit(out);
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        {visible.map((f) => {
          const v = values[f.name];
          const opts = typeof f.options === "function" ? f.options() : (f.options ?? []);
          return (
            <Field key={f.name} label={f.label + (f.required ? " *" : "")} hint={f.hint}>
              {f.type === "switch" ? (
                <Switch checked={Boolean(v)} onChange={(c) => set(f.name, c)} />
              ) : f.type === "textarea" ? (
                <textarea className="input" rows={3} value={String(v ?? "")} onChange={(e) => set(f.name, e.target.value)} />
              ) : f.type === "select" ? (
                <select className="input" value={String(v ?? "")} onChange={(e) => set(f.name, e.target.value)} required={f.required}>
                  <option value="">—</option>
                  {opts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : f.type === "image" ? (
                <ImageField value={String(v ?? "")} onChange={(url) => set(f.name, url)} />
              ) : f.type === "multiselect" ? (
                <select
                  className="input"
                  multiple
                  size={Math.min(6, Math.max(3, opts.length))}
                  value={(v as string[]) ?? []}
                  onChange={(e) => set(f.name, Array.from(e.target.selectedOptions, (o) => o.value))}
                >
                  {opts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  type={f.type === "number" ? "number" : f.type === "color" ? "color" : f.type === "date" ? "date" : f.type === "password" ? "password" : "text"}
                  step={f.type === "number" ? "any" : undefined}
                  value={String(v ?? "")}
                  placeholder={f.placeholder}
                  required={f.required && isNew}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              )}
            </Field>
          );
        })}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** URL field with an upload button: the file goes to storage as an admin
 * asset and the resulting public URL is stored in the field. */
function ImageField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadFile(file, { owner_type: "asset" }));
      toast("Image uploaded");
    } catch (e) {
      setError(e instanceof Error ? e.message : "upload failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="image-field">
      {value && <img src={value} alt="" className="thumb" />}
      <input className="input" value={value} placeholder="https://… or upload" onChange={(e) => onChange(e.target.value)} />
      <label className="btn btn-ghost btn-sm">
        {busy ? "Uploading…" : "Upload"}
        <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => pick(e.target.files?.[0])} />
      </label>
      {error && <small className="error">{error}</small>}
    </div>
  );
}
